import axios, { AxiosRequestConfig } from 'axios';
import { endOfToday, startOfToday } from 'date-fns';
import { CreateTravelSetRequest } from 'generated';

import {
  CLAItem,
  DCCType
} from '../../components/admin-portal/CLA/CLASettings/CLASettings.types';
import { ApiAccounts } from '../../components/admin-portal/Grants/ApiAccounts/types';
import {
  CronJob,
  CronJobDefinition,
  ImportDefinitions
} from '../../components/admin-portal/Procedures/CronJobs/CronJobsCRUDForm.types';
import { YearClosure } from '../../components/admin-portal/Procedures/YearClosure/types';
import { MaxCLASetting } from '../../components/admin-portal/Tables/CLA/MaxCLASettings/types';
import {
  Bonus25Method,
  BonusType,
  PremiumPay
} from '../../components/admin-portal/Tables/CLA/PremiumPay/types';
import { SalaryScaleResponse } from '../../components/admin-portal/Tables/CLA/SalaryScales/types';
import { DayLimitArrangementResponse } from '../../components/admin-portal/Tables/CLA/DayLimitArrangements/types';
import {
  BuhHandlingOptions,
  Employer,
  EmployerCostSettingResponse,
  EmployerCostSettingsRequest,
  EmployerCostSettingsResponse
} from '../../components/admin-portal/Tables/Employer/EmployerCostSettings/EmployerCostSettings.types';
import { Employers } from '../../components/admin-portal/Tables/Employer/Employers/types';
import {
  CostCenters,
  SurchargeRelations,
  SurchargeRelationsRequest,
  Surcharges
} from '../../components/admin-portal/Tables/Employer/SurchargeRelations/types';
import {
  ServiceBrOption,
  SurchargesMaintenance,
  SurchargesMaintenanceDetails,
  VatOption
} from '../../components/admin-portal/Tables/Employer/SurchargesMaintenance/types';
import {
  AdminFunction,
  FunctionFlag
} from '../../components/admin-portal/Tables/Settings/AdminFunctions/AdminFunctions.types';
import {
  ClusterData,
  ClusterDataPreferred
} from '../../components/admin-portal/Tables/Settings/Clusters/types';
import { DHTClaSettings } from '../../components/admin-portal/Tables/Settings/DHTClaSettings/types';
import {
  BrCategory,
  BrType,
  CostCenterDetail,
  PlanUnit
} from '../../components/admin-portal/Tables/Settings/PlanUnits/types';
import { IQualification } from '../../components/admin-portal/Tables/Settings/Qualifications/types';
import {
  Arrangement,
  TravelCompensationType
} from '../../components/admin-portal/Tables/TravelCosts/Arrangements/types';
import { Distance } from '../../components/admin-portal/Tables/TravelCosts/Distances/types';
import { AgeHoursTableData } from '../../components/admin-portal/Tables/Year/AgeHours/types';
import {
  HolidayHours,
  PeriodTypes
} from '../../components/admin-portal/Tables/Year/HolidayHours/types';
import { ConvertedHoliday } from '../../components/admin-portal/Tables/Year/Holidays/types';
import { IHolidaysSpecial } from '../../components/admin-portal/Tables/Year/HolidaysSpecial/types';
import { CLAOption, DateString, User, UserAccount } from 'types';

import { FunctionGroupsResponse } from '../../components/admin-portal/Tables/Settings/AdminFunctions/utils/utils';
import { toLocalISOString } from 'utils/date';

import { GridRowId } from '@mui/x-data-grid-pro';

import {
  AccountGroupData,
  BaseReport,
  Contract,
  CostCenter,
  Employee
} from '../../components/admin-portal/Procedures/Reports/type';
import { DutyCode } from '../../components/admin-portal/Settings/DutyCodes/type';

import {
  ACCOUNT_GROUPS,
  ADMIN_CLA_SETTINGS,
  ADMIN_REPORTS,
  ADMIN_SETTINGS_FUNCTIONS,
  ADMIN_SETTINGS_GROUP,
  ADMINISTRATION_ACCOUNTS,
  ADMINISTRATION_EMPLOYEES,
  ADVANCED_REPORTS,
  AFAS_CONNECTORS,
  AGE_HOURS,
  API_ACCOUNTS,
  APPROVE_DUTY_LINES,
  ARRANGEMENTS,
  BALANCE_REPORTS,
  BASE_REPORTS,
  BONUS_25_METHOD,
  BONUS_TYPES,
  BR_CATEGORIES,
  BR_TYPES,
  BUH_HANDLINGS,
  CALCULATE_DUTY_LINES,
  CLA_OPTIONS,
  CLA_YEAR_SETTINGS,
  CLUSTERS,
  CONTRACTS,
  COST_CENTER_DETAILS,
  COST_CENTERS_BASE,
  CREATE_ARRANGEMENT,
  CRON_JOB_DEFINITIONS,
  CRON_JOBS,
  DCC_OPTIONS,
  DHT_CLA_SETTINGS,
  DISTANCES,
  GENERATE_REALIZATION_REPORT,
  DUTY_CODES,
  DUTY_LINES_SUMMARY,
  DUTY_PERIODS,
  EMPLOYEES_WITH_USERNAME,
  EMPLOYER,
  EMPLOYER_COST_SETTINGS,
  EMPLOYERS,
  GENERATE_SALARY_COMPONENTS,
  GET_USERNAME_FOR_BI,
  GRANTS,
  GRANTS_CATALOG,
  HIERARCHY,
  HOLIDAY_HOURS,
  HOLIDAYS,
  IMPORT_DEFINITIONS,
  MODULES,
  PERIOD_TYPES,
  PLAN_UNIT_DETAILS,
  PLAN_UNITS,
  PREMIUM_PAYS,
  PUBLIC_COSTCENTERS,
  PUBLIC_HOLIDAYS,
  QUALIFICATIONS,
  REALIZATION,
  RECALCULATE_HOUR_MANAGEMENT,
  ROSTER_DUTY_HOUR_TYPES,
  S3UPLOAD,
  SALARY_SCALES,
  DAY_LIMIT_ARRANGEMENTS,
  SAVE_PWD_FOR_BI,
  SERVICE_BR_OPTIONS,
  SERVICE_DESK_REPORTS,
  SPECIALS_HOLIDAYS,
  SURCHARGE_RELATIONS,
  SURCHARGES,
  SURCHARGES_MAINTENANCE,
  SYS_ADMIN_SYNC,
  TRAVEL_COMPENSATION_TYPE,
  VAT_OPTIONS,
  YEAR_CLOSURE,
  CLUSTERS_LINKED,
  COSTCENTER_SETTINGS_GROUP,
  TRAVEL_MOVEMENTS_EXPORT,
  CALCULATE_DUTY_LINES_EMPLOYEE
} from '../endpoints';
import {
  PasswordSave,
  UsernameObj
} from '../../components/admin-portal/Settings/DatabaseView/types';
import {
  Account,
  AccountDetails,
  AdministrationEmployees
} from '../../components/types';
import { convertToValid } from 'utils/convertToValid';
import { CostCenterDutyHourType } from '../../components/admin-portal/Settings/AbsenceImport/types';
import {
  Employeer,
  ImportEmployeeSetting
} from '../../components/admin-portal/Settings/EmployeeSettings/types';
import { TravelMovementsExportData } from 'components/admin-portal/Tables/Settings/TravelMovementsExport/types';

export * from 'RestAPIs/admin/costCenter';

export type CalculateDutyLinesOptions = {
  startDate: Date;
  endDate: Date;
};
export const calculateDutyLines = ({
  startDate,
  endDate
}: CalculateDutyLinesOptions) =>
  axios.post(CALCULATE_DUTY_LINES, {
    startDate: toLocalISOString(startDate),
    endDate: toLocalISOString(endDate)
  });

export type CalculateDutyLinesForEmployeeOptions = {
  startDate: Date;
  endDate: Date;
  employeeId: number;
};
export const calculateDutyLinesForEmployee = ({
  startDate,
  endDate,
  employeeId
}: CalculateDutyLinesForEmployeeOptions) => {
  return axios.post(
    CALCULATE_DUTY_LINES_EMPLOYEE,
    {
      startDate: toLocalISOString(startDate),
      endDate: toLocalISOString(endDate)
    },
    {
      pathParams: {
        employeeId
      }
    }
  );
};

export type RealizeDutyLinesOptions = {
  startDate: Date;
  endDate: Date;
  lockMonth: boolean;
};
export const realizeDutyLines = ({
  startDate,
  endDate,
  lockMonth
}: RealizeDutyLinesOptions) =>
  axios.put(REALIZATION, null, {
    params: {
      startDate: toLocalISOString(startDate),
      endDate: toLocalISOString(endDate),
      lockMonth
    }
  });

export type BulkApproveDutyLinesOptions = {
  startDate: Date;
  endDate: Date;
  withApprovalFlow: boolean;
};
export const bulkApproveDutyLines = ({
  startDate,
  endDate,
  withApprovalFlow
}: BulkApproveDutyLinesOptions) =>
  axios.post(APPROVE_DUTY_LINES, null, {
    params: {
      startDate: toLocalISOString(startDate),
      endDate: toLocalISOString(endDate),
      withApprovalFlow
    }
  });

export type GetDutyLinesSummaryOptions = {
  startDate: DateString;
  endDate: DateString;
  aggregateOn: string;
};

type DutyLinesSummaryPeriod = {
  periodResponse: {
    number: number;
    quarter: number;
    year: number;
  };
  nrOfNotRealizedDuties: number;
  nrOfNotCalculatedDuties: number;
  nrOfRejectedDuties: number;
  nrOfPendingDuties: number;
  nrOfBlockedDuties: number;
  nrOfRealizableDuties: number;
  nrOfNotRealizableDuties: number;
  nrOfPNILInvoicedDuties: number;
  nrOfPNILNotInvoicedDuties: number;
  totalNrOfDuties: number;
};

export type GetDutyLinesSummaryResponse = {
  dutiesRealizationSummaryPeriods: DutyLinesSummaryPeriod[];
};

export const getDutyLinesSummary = ({
  startDate,
  endDate,
  aggregateOn
}: GetDutyLinesSummaryOptions) =>
  axios.get<GetDutyLinesSummaryResponse>(DUTY_LINES_SUMMARY, {
    params: { startDate, endDate, aggregateOn }
  });

export type generateRealizationReportOptions = {
  validFrom: Date;
  validTo: Date;
};

export const generateRealizationReport = ({
  validFrom,
  validTo
}: generateRealizationReportOptions) =>
  axios.get<number>(GENERATE_REALIZATION_REPORT, {
    params: {
      valid_from: toLocalISOString(validFrom),
      valid_to: toLocalISOString(validTo),
      not_ready_for_realisation_flag: true,
      dub_trv_flag: false
    }
  });

export const recalculateHourManagement = (year: number) =>
  axios.put(`${RECALCULATE_HOUR_MANAGEMENT}/${year}`);

/** Recalculates hours management for a single work agreement (the employee-card variant). */
export const recalculateHoursForWorkAgreement = (
  year: number,
  workAgreementId: number
) => axios.put(`${RECALCULATE_HOUR_MANAGEMENT}/${year}/${workAgreementId}`);

export const getSettingsGroup = (groupName?: string) =>
  axios.get(ADMIN_SETTINGS_GROUP, {
    params: {
      groupName
    }
  });

export const editSettingsGroup = (data) =>
  axios.put(`${ADMIN_SETTINGS_GROUP}/${data.groupId}`, data);

export const getCostCenterSettings = (
  groupName?: string,
  costCenterId?: number
) =>
  axios.get(COSTCENTER_SETTINGS_GROUP, {
    params: {
      groupName,
      costCenters: costCenterId
    }
  });

export const getEmployerCostSettings = () =>
  axios.get<EmployerCostSettingsResponse[]>(EMPLOYER_COST_SETTINGS);

export const deleteEmployerCostSettings = (id: number) =>
  axios.delete(`${EMPLOYER_COST_SETTINGS}/${id}`);

export const getEmployerCostSettingById = (id: number) =>
  axios.get<EmployerCostSettingResponse>(`${EMPLOYER_COST_SETTINGS}/${id}`);

export const createEmployerCostSettings = (data: EmployerCostSettingsRequest) =>
  axios.post(EMPLOYER_COST_SETTINGS, data);

export const editEmployerCostSettings = (data: EmployerCostSettingsRequest) =>
  axios.put(`${EMPLOYER_COST_SETTINGS}/${data.employerDynId}`, data);

export const getEmployersForSelect = () => axios.get<Employer[]>(EMPLOYER);

export const getApiAccounts = () => axios.get<ApiAccounts[]>(API_ACCOUNTS);

export const getApiAccountById = (id) => axios.get(`${API_ACCOUNTS}/${id}`);
export const generateApiKey = (accountId: number) =>
  axios.post(`${API_ACCOUNTS}/generate`, { accountId: accountId });

export const getDHTClaSettings = () =>
  axios.get<DHTClaSettings[]>(DHT_CLA_SETTINGS);

export const getDHTClaSettingsById = (id: number | undefined) =>
  axios.get<DHTClaSettings>(`${DHT_CLA_SETTINGS}/${id}`);

export const generateSalaryComponents = () =>
  axios.put(GENERATE_SALARY_COMPONENTS);

export const getSalaryScales = () =>
  axios.get<SalaryScaleResponse[]>(SALARY_SCALES);

export const getDayLimitArrangements = () =>
  axios.get<DayLimitArrangementResponse[]>(DAY_LIMIT_ARRANGEMENTS);

export const getCLAOptions = () => axios.get<CLAOption[]>(CLA_OPTIONS);

export const getSurchargeRelations = () =>
  axios.get<SurchargeRelations[]>(SURCHARGE_RELATIONS);

export const getSurchargeRelationById = (id: number) =>
  axios.get<SurchargeRelations>(`${SURCHARGE_RELATIONS}/${id}`);

export const getSurcharges = () => axios.get<Surcharges[]>(SURCHARGES);

export const getSysAdminSync = () => axios.get(SYS_ADMIN_SYNC);

export const editSurchargeRelation = (data: SurchargeRelationsRequest) =>
  axios.put(`${SURCHARGE_RELATIONS}/${data.brEmployerLinkClientId}`, data);

export const createSurchargeRelation = (data: SurchargeRelationsRequest) =>
  axios.post(`${SURCHARGE_RELATIONS}`, data);

export const deleteSurchargeRelation = ([id]: GridRowId[]) =>
  axios.delete(`${SURCHARGE_RELATIONS}/${id}`);

export const getCostCenters = () => {
  const startDate = new Date('0001-01-01').toISOString();
  const endDate = new Date('9999-12-31').toISOString();
  return axios.get<CostCenters[]>(
    `${PUBLIC_COSTCENTERS}?startDate=${startDate}&endDate=${endDate}`
  );
};

export const getCostCentersWithGivenInterval = (start: string, end: string) => {
  const startDate = start
    ? new Date(start).toISOString()
    : new Date().toISOString();
  const endDate = end ? new Date(end).toISOString() : null;
  return axios.get(
    endDate
      ? `${PUBLIC_COSTCENTERS}?startDate=${startDate}&endDate=${endDate}`
      : `${PUBLIC_COSTCENTERS}?startDate=${startDate}`
  );
};

export const getFunctionGroupOptions = () =>
  axios.get<FunctionGroupsResponse[]>(ADMIN_SETTINGS_FUNCTIONS, {
    params: {
      flags: 'FUNCTION_GROUP',
      description: '**'
    }
  });

export const getAdminFunctionById = (id?: number) =>
  axios.get(`${ADMIN_SETTINGS_FUNCTIONS}/${id}`);

export const getAdminFunctions = () =>
  axios.get<AdminFunction[]>(ADMIN_SETTINGS_FUNCTIONS);

export const getAdminFunctionGroups = (flag: FunctionFlag) =>
  axios.get<AdminFunction[]>(ADMIN_SETTINGS_FUNCTIONS, {
    params: {
      flag
    }
  });

export const getQualifications = () => axios.get(QUALIFICATIONS);

export const getQualificationsById = (id: string) =>
  axios.get(`${QUALIFICATIONS}/${id}`);

export const editQualification = (data: IQualification) =>
  axios.put(`${QUALIFICATIONS}/${data.id}`, data);

export const getHolidayHours = () => axios.get<HolidayHours[]>(HOLIDAY_HOURS);

export const getBuhHandlings = () =>
  axios.get<BuhHandlingOptions[]>(BUH_HANDLINGS);

export const getEmployers = () => axios.get<Employers[]>(EMPLOYERS);
export const getParentsUnitsDetails = () => axios.get<any[]>(PLAN_UNIT_DETAILS);

export const getEmployees = () =>
  axios.get<Employee[]>(EMPLOYEES_WITH_USERNAME);

export const getPeriodTypes = () => axios.get<PeriodTypes[]>(PERIOD_TYPES);

export const getHolidaysSpecial = () =>
  axios.get<IHolidaysSpecial[]>(SPECIALS_HOLIDAYS);

export const getPremiumPays = () => axios.get<PremiumPay[]>(PREMIUM_PAYS);

export const getBonusTypes = () => axios.get<BonusType[]>(BONUS_TYPES);

export const getBonus25Methods = () =>
  axios.get<Bonus25Method[]>(BONUS_25_METHOD);

export const getPremiumPayById = (id?: number | null) =>
  axios.get<PremiumPay>(`${PREMIUM_PAYS}/${id}`);

export const getPlanUnitsList = () => axios.get<PlanUnit[]>(PLAN_UNITS);

export const getBrTypes = () => axios.get<BrType[]>(BR_TYPES);

export const getBrCategories = () => axios.get<BrCategory[]>(BR_CATEGORIES);

export const getHolidays = () => axios.get<ConvertedHoliday[]>(PUBLIC_HOLIDAYS);

export const getHolidayById = (id) =>
  axios.get<ConvertedHoliday>(`${HOLIDAYS}/${id}`);

export const getAgeHours = () => axios.get<AgeHoursTableData[]>(AGE_HOURS);

export const getAgeHourById = (id) =>
  axios.get<AgeHoursTableData>(`${AGE_HOURS}/${id}`);

export const getMaxCLASettings = () =>
  axios.get<MaxCLASetting[]>(ADMIN_CLA_SETTINGS);

export const getMaxCLASettingById = (id) =>
  axios.get(`${ADMIN_CLA_SETTINGS}/${id}`);

export const getSurchargesMaintenance = () =>
  axios.get<SurchargesMaintenance[]>(SURCHARGES_MAINTENANCE);

export const getSurchargesMaintenanceById = (id) =>
  axios.get<SurchargesMaintenance>(`${SURCHARGES_MAINTENANCE}/${id}`);

export const createSurchargesMaintenanceDetails = (data) =>
  axios.post<SurchargesMaintenanceDetails>(
    `${SURCHARGES_MAINTENANCE}/${data.surchargeId}/surcharges-dyn`,
    data
  );

export const saveSurchargesMaintenanceDetails = (
  data: SurchargesMaintenanceDetails
) =>
  axios.put<SurchargesMaintenanceDetails>(
    `${SURCHARGES_MAINTENANCE}/${data.surchargeId}/surcharges-dyn/${data.surchargeDynId}`,
    data
  );

export const deleteSurchargesMaintenanceDetails = (
  data: SurchargesMaintenanceDetails,
  id
) =>
  axios.delete<SurchargesMaintenanceDetails>(
    `${SURCHARGES_MAINTENANCE}/${data.surchargeId}/surcharges-dyn/${id}`
  );

export const getSurchargesMaintenanceDetailsById = (id) =>
  axios.get<SurchargesMaintenance>(
    `${SURCHARGES_MAINTENANCE}/${id}/surcharges-dyn`
  );

export const editSurchargesMaintenance = (data: SurchargesMaintenance) =>
  axios.put<SurchargesMaintenance>(
    `${SURCHARGES_MAINTENANCE}/${data.surchargeId}`,
    data
  );

export const deleteSurchargesMaintenance = (id: number) =>
  axios.delete(`${SURCHARGES_MAINTENANCE}/${id}`);

export const getArrangements = () =>
  axios.get<Arrangement[]>(`${ARRANGEMENTS}`);

export const getArrangement = (id: number) =>
  axios.get<Arrangement>(`${ARRANGEMENTS}/${id}`);

export const createArrangement = (data: CreateTravelSetRequest) =>
  axios.post(`${CREATE_ARRANGEMENT}`, data);

export const deleteArrangement = (id: number) =>
  axios.delete(`${ARRANGEMENTS}/${id}`);

export const getTravelCompensationType = () =>
  axios.get<TravelCompensationType[]>(TRAVEL_COMPENSATION_TYPE);

export const getTravelMovementsExport = () =>
  axios.get<TravelMovementsExportData[]>(TRAVEL_MOVEMENTS_EXPORT);

export const getYearClosure = () => axios.get<YearClosure>(YEAR_CLOSURE);

export const closeCurrentYear = () =>
  axios.post(YEAR_CLOSURE, { ignoreErrors: false });

export const openCurrentYear = () => axios.delete(YEAR_CLOSURE);

export const getVatOptions = () => axios.get<VatOption[]>(VAT_OPTIONS);

export const getServiceBrOptions = () =>
  axios.get<ServiceBrOption[]>(
    `${SERVICE_BR_OPTIONS}?name=*&startDate=${toLocalISOString(
      startOfToday()
    )}&endDate=${toLocalISOString(endOfToday())}`
  );

export const getDistances = () => axios.get<Distance[]>(DISTANCES);

export const getDistanceById = (id) =>
  axios.get<Distance>(`${DISTANCES}/${id}`);

export const getDutyCodes = () => axios.get<DutyCode[]>(DUTY_CODES);
export const getDutyTypes = () => axios.get(ROSTER_DUTY_HOUR_TYPES);
export const getDutyCode = (id) => axios.get<DutyCode>(`${DUTY_CODES}/${id}`);
export const createDutyCode = (data) => axios.post(DUTY_CODES, data);
export const createDutyPeriods = (data) => axios.post(DUTY_PERIODS, data);
export const editDutyCode = (data) =>
  axios.put(`${DUTY_CODES}/${data.dutyId}`, data);

export const getClusters = () => axios.get<ClusterData[]>(CLUSTERS);

export const addCluster = (brId: number, clusterId: number) => {
  return axios.post(`${CLUSTERS_LINKED}`, {
    brId,
    clusterId
  });
};

export const removeCluster = (brLinkClusterId: number) => {
  return axios.delete(`${CLUSTERS_LINKED}/${brLinkClusterId}`);
};

export const getClustersPreferred = (brId: number) =>
  axios.get<ClusterDataPreferred[]>(`${CLUSTERS_LINKED}?brId=${brId}`);

export const getPlanUnitDetails = (id: number) =>
  axios.get<CostCenterDetail[]>(`${COST_CENTER_DETAILS}?brId=${id}`);

export const getHierarchy = () => axios.get<PlanUnit[]>(HIERARCHY);

export const getClusterById = (id: number) =>
  axios.get<ClusterData>(`${CLUSTERS}/${id}`);

export * from './costCenter';

export const getCLASetting = (id: number) =>
  axios.get<any[]>(`${CLA_YEAR_SETTINGS}/${id}`);

export const getCLAYearSettings = () => axios.get<CLAItem[]>(CLA_YEAR_SETTINGS);
export const getDccOptions = () => axios.get<DCCType[]>(DCC_OPTIONS);

export const getBasicReports = () => axios.get<BaseReport[]>(BASE_REPORTS);

export const getCostCentersWithoutFilter = () =>
  axios.get<CostCenter[]>(COST_CENTERS_BASE);

export const getContracts = () => axios.get<Contract[]>(CONTRACTS);

export const getURLForFileUpload = (filename: string) =>
  axios.get<{ uploadUrl: string }>(S3UPLOAD, {
    params: { filename: filename }
  });

export const getCronJobs = () => axios.get<CronJob[]>(CRON_JOBS);

export const getCronJobDefinitions = () =>
  axios.get<CronJobDefinition[]>(CRON_JOB_DEFINITIONS);

export const getImportDefinitions = (params?: AxiosRequestConfig['params']) =>
  axios
    .get<ImportDefinitions[]>(IMPORT_DEFINITIONS, {
      params: params ?? { description: '**' }
    })
    .then(({ data }) => data);

export const getGrantsModules = () => axios.get(MODULES);

export const getAdvancedReports = () =>
  axios.get<BaseReport[]>(ADVANCED_REPORTS);

export const getAdminReports = () => axios.get<BaseReport[]>(ADMIN_REPORTS);

export const getBalancedReports = () =>
  axios.get<BaseReport[]>(BALANCE_REPORTS);

export const getAccountGroups = () =>
  axios.get<AccountGroupData[]>(ACCOUNT_GROUPS);
export const getServiceDeskReports = () =>
  axios.get<BaseReport[]>(SERVICE_DESK_REPORTS);
export const getUsernameForBI = () =>
  axios.get<UsernameObj>(GET_USERNAME_FOR_BI);
export const createPwdForBI = (data: PasswordSave) =>
  axios.put<PasswordSave>(SAVE_PWD_FOR_BI, {
    password: data.password,
    'password.retype': data.retypePassword
  });

export const getCostCenterDutyHourTypes = () =>
  axios.get<CostCenterDutyHourType[]>(ROSTER_DUTY_HOUR_TYPES);

export const getAdministrationAccounts = async () =>
  await axios.get<Account[]>(ADMINISTRATION_ACCOUNTS).then(({ data }) => data);

export const getAdministrationAccountDetails = async (id: number | string) =>
  await axios
    .get<AccountDetails>(`${ADMINISTRATION_ACCOUNTS}/${id}`)
    .then<AccountDetails>(({ data }) => {
      return {
        ...data,
        valid: convertToValid(data.valid),
        groups: data.groups.map((group) => ({
          ...group,
          valid: convertToValid(group.valid)
        }))
      };
    });

export const getAdministrationEmployees = async (noAccount = true) =>
  await axios
    .get<AdministrationEmployees[]>(ADMINISTRATION_EMPLOYEES, {
      params: { noAccount }
    })
    .then(({ data }) => data);

export const getGrantsCatalog = (queryParams?: AxiosRequestConfig['params']) =>
  axios.get(GRANTS_CATALOG, { params: queryParams });

export const getGrants = (queryParams?: AxiosRequestConfig['params']) =>
  axios.get(GRANTS, { params: queryParams });

export const createGrant = (grant) => axios.post(GRANTS, grant);

export const deleteGrant = (grantId: number) =>
  axios.delete(`${GRANTS}/${grantId}`);

export const getCostCenterGrantsByGroup = (groupId: number) =>
  axios.get(
    `secure/{username}/administration/maintenance/groups/${groupId}/cost-center-grants?groupId=${groupId}`
  );

export const editCostCenterGrantByGroup = (
  groupId: number,
  brId: number,
  data
) =>
  axios.put(
    `secure/{username}/administration/maintenance/groups/${groupId}/cost-center-grants/${brId}`,
    data
  );

export const deleteCostCenterGrantByGroup = (groupId: number, brId: number) =>
  axios.delete(
    `secure/{username}/administration/maintenance/groups/${groupId}/cost-center-grants/${brId}`
  );

export const getAccountsByGroup = (groupId: number) =>
  axios.get<UserAccount[]>(
    `secure/{username}/administration/maintenance/groups/${groupId}/accounts?groupId=${groupId}`
  );

export const editAccountByGroup = (groupId: number, accountId: number, data) =>
  axios.put(
    `secure/{username}/administration/maintenance/groups/${groupId}/accounts/${accountId}`,
    data
  );

export const deleteAccountByGroup = (groupId: number, accountId: number) =>
  axios.delete(
    `secure/{username}/administration/maintenance/groups/${groupId}/accounts/${accountId}`
  );

export const getUsers = () => axios.get<User[]>('secure/users');

export const addAccountSync = (data) =>
  axios.post('secure/users/{username}/employees/sync-accounts', data);

export const getImportEmployeeConfig = () =>
  axios.get<ImportEmployeeSetting[]>(
    `/secure/{username}/administration/maintenance/importEmployeeConfig`
  );

export const editImportEmployeeConfig = (data) =>
  axios.put<ImportEmployeeSetting[]>(
    `/secure/{username}/administration/maintenance/importEmployeeConfig`,
    data
  );

export const editEnityMappings = (data) =>
  axios.post(
    `/secure/admin/{username}/imports/AFAS/entity-mappings/${data.entityMapping}`,
    data
  );

export const editConnectorConfig = (data) => axios.put(AFAS_CONNECTORS, data);

export const getImportEmployeeConfigEmployer = () =>
  axios.get<Employeer[]>(
    `/secure/{username}/administration/maintenance/importEmployeeConfigEmployer`
  );
